import { YesNoOptionLabel } from '@caresend/types';

/**
 * (e.g) stripPunctuation('PST lt. Green tube') => PST lt Green tube
 */

const stripPunctuation = str => str.replace(/[.,?!]/g, '');
/**
 * (e.g) splitPascalCase('PumposhIsCool') => 'Pumposh Is Cool'
 */

const splitPascalCase = s => s.replace(/([a-z])([A-Z])/g, '$1 $2');
/**
 * (e.g) strToPascalCase('Pumposh Is Cool') => 'PumposhIsCool'
 */

const strToPascalCase = str => (' ' + str).toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_m, chr) => chr.toUpperCase());
/**
 * (e.g) strToCamelCase('Pumposh Is Cool') => 'pumposhIsCool'
 */

const strToCamelCase = str => str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase()).replace(/\s+/g, '');
/**
 * (e.g) capitalize('hello world!') => 'Hello world!'
 * Returns empty string if value undefined or null
 */

const capitalize = str => !str ? '' : str.charAt(0).toUpperCase() + str.slice(1);
/**
 * (e.g.) buildBulletSeparatedString(['Hello', 'World!']) => Hello • World!
 */

const buildBulletSeparatedString = (items, separator = '•') => {
  const filteredItems = items.filter(item => !!item);
  return filteredItems.reduce((previousVal, item, index) => {
    const bulletPrefix = index !== 0 ? ` ${separator} ` : '';
    return `${previousVal}${bulletPrefix}${item}`;
  }, '');
};
/**
 * (e.g.) camelCaseToTitleCase('testTest') => Test Test
 */

const camelCaseToTitleCase = str => capitalize(str.replace(/([a-z0-9])([A-Z])/g, '$1 $2'));
/**
 * (e.g.) camelCaseToSentenceCase('testTest') => Test test
 */

const camelCaseToSentenceCase = str => capitalize(str.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());
/**
 * Example:
 *
 * (0, 'test') => 'tests'
 * (1, 'test') => 'test'
 * (2, 'test') => 'tests'
 */

const getPlural = (count, entityName) => {
  let plural = 's';
  if (count === 1) plural = '';
  return `${entityName}${plural}`;
};
/**
 * Example:
 *
 * (0, 'test') => 'No tests'
 * (0, 'test', true) => '0 tests'
 * (1, 'test') => '1 test'
 * (2, 'test') => '2 tests'
 */

const getPluralCount = (count, entityName,
/** Use 0 instead of the word 'No' if the count is 0 */
use0 = false) => {
  let countStr = String(count);
  if (count === 0 && !use0) countStr = YesNoOptionLabel.NO;
  return `${countStr} ${getPlural(count, entityName)}`;
};
/**
 * Example:
 *
 * getLastCharactersOfString('123456', 2) => '56'
 */

const getLastCharactersOfString = (str, n) => {
  if (!str) return;
  return str.slice(str.length - n);
};
/**
 * Social security number validator
 */

const ssnValidator = value => !!value && /^(?!(000|666|9))(\d{3}-?(?!(00))\d{2}-?(?!(0000))\d{4})$/.test(value);
var Separator;

(function (Separator) {
  Separator["COMMA"] = "comma";
  Separator["HYPHEN"] = "hyphen";
  Separator["PIPE"] = "pipe";
})(Separator || (Separator = {}));

const literalSeparator = {
  [Separator.COMMA]: ', ',
  [Separator.HYPHEN]: ' - ',
  [Separator.PIPE]: ' | '
};
/**
 * US bank acount: Routing number validator
 */

const routingNumberValidator = value => !!value && /^\b((0[0-9])|(1[0-2])|(2[1-9])|(3[0-2])|(6[1-9])|(7[0-2])|80)([0-9]{7})\b$/.test(value);
/**
 * US bank acount: Account number validator
 */

const accountNumberValidator = value => !!value && /^[0-9]{7,14}$/.test(value);
/**
 * Example: production-staging => Production Staging
 */

const formatKebabCase = str => str.split('-').map(capitalize).join(' ');
/**
 * Converts a given string to camelCase format.

 * Example:
 * ```typescript
 * formatCamelCase("helloWorld"); // "Hello World"
 * formatCamelCase("formatCamelCase"); // "Format Camel Case"
 * ```
 */

const formatCamelCase = str => capitalize(str.replace(/([a-z0-9])([A-Z])/g, '$1 $2'));
/**
 * Example: production_staging => Production staging
 */

const formatSnakeCase = str => capitalize(str.split('_').join(' '));
/**
 * Check if a string is a valid email address
 */

const emailIsValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
/**
 * Check if a string is a valid NPI number
 */

const isNPIValid = npi => npi.length === 10 && Number.isInteger(Number(npi));
/**
 * Check if a string is a valid name, including some special characters
 */

const isNameValid = name => /^[a-zA-Z\s.,"'/|]+$/.test(name) && !!name.length;
const isOfficeNameValid = name => /^[a-zA-Z0-9\s.,"'/|]+$/.test(name) && !!name.length;
const isMiddleInitialValid = middleInitial => !middleInitial.length || /^[a-zA-Z]+$/.test(middleInitial) && middleInitial.length === 1;
/**
 * Check if a string is a valid medical license number
 */

const isMedicalLicenseNumberValid = licenseNumber => /^[a-zA-Z0-9-]+$/.test(licenseNumber) && !!licenseNumber.length;
/**
 * Remove any special character from a string
 * Input: "Hello! How are you? This is a #test. Don't worry!"
 * Output: "Hello How are you This is a test Dont worry"
 */

const removeSpecialCharacters = (input, replaceWith) => {
  // Define a regular expression to match special characters
  const specialCharsRegex = /[^\w\s]/g; // Replace special characters with an empty string

  const result = input.replace(specialCharsRegex, replaceWith !== null && replaceWith !== void 0 ? replaceWith : '');
  return result;
};
/**
 * Remove invalid characters from an email address
 */

const removeInvalidEmailCharacters = email => email.replace(/[^a-zA-Z0-9._%+-@]/g, '');

export { Separator, accountNumberValidator, buildBulletSeparatedString, camelCaseToSentenceCase, camelCaseToTitleCase, capitalize, emailIsValid, formatCamelCase, formatKebabCase, formatSnakeCase, getLastCharactersOfString, getPlural, getPluralCount, isMedicalLicenseNumberValid, isMiddleInitialValid, isNPIValid, isNameValid, isOfficeNameValid, literalSeparator, removeInvalidEmailCharacters, removeSpecialCharacters, routingNumberValidator, splitPascalCase, ssnValidator, strToCamelCase, strToPascalCase, stripPunctuation };
